import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Layout, Typography, Select, Spin, Divider,
  Empty, Tooltip, Button, Pagination, Input, Avatar, Skeleton, message as antdMessage
} from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import userStore from '../../../zustand/store/userStore';
import AppHeader from '../../../components/Dashboard/AppHeader';
import { getChildsAPI, getSingleStudentResultAPI } from '../../../zustand/API';
import { debounce } from '../../../helper';
import studentbar from '../../../assets/icons/studentbar.svg';



const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const QuizCard = ({ result, selectedChild, onReport }) => {
  const statusColor = {
    Pass: 'bg-green-100 text-green-600',
    'Need Improvment': 'bg-yellow-100 text-yellow-600',
    Fail: 'bg-red-100 text-red-600',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow p-5 hover:shadow-lg transition-all">
      <div className="flex items-center gap-3 mb-4">
        <Avatar size={40} src={selectedChild?.profilePic}>
          {selectedChild?.fullName?.charAt(0).toUpperCase()}
        </Avatar>
        <div>
          <Tooltip title={result.email}>
            <p className="font-medium text-gray-800 truncate max-w-[180px]">{result.email}</p>
          </Tooltip>
          <p className="text-sm text-gray-500">Instructor</p>
        </div>
      </div>

      <div className="mb-2">
        <p className="text-xs text-gray-500">Subject</p>
        <p className="text-sm font-medium text-gray-800">{result.course}</p>
      </div>

      <div className="mb-2">
        <p className="text-xs text-gray-500">Chapter</p>
        <p className="text-sm text-gray-700">{result.quizTaken}</p>
      </div>

      <div className="grid grid-cols-3 text-center my-4">
        <div><p className="text-xs text-gray-500">Total</p><p className="font-bold text-gray-700">{result.totalQuestions}</p></div>
        <div><p className="text-xs text-gray-500">Correct</p><p className="font-bold text-blue-600">{result.marks}</p></div>
        <div><p className="text-xs text-gray-500">Percent</p><p className="font-bold text-purple-600">{result.percentage}%</p></div>
      </div>

      <div className="flex justify-between items-center">
        <Tooltip title="View Report">
          <Button
            type="text"
            icon={<BarChartOutlined />}
            className="text-blue-600 hover:bg-blue-50"
            onClick={() => onReport({ email: selectedChild?.email, subjectName: result.course, title: result.quizTaken })}
          />
        </Tooltip>
        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColor[result.status] || 'bg-gray-100 text-gray-600'}`}>
          {result.status}
        </div>
      </div>
    </div>
  );
};

const ChildProfileCard = ({ child }) => (
  <div className="bg-white shadow-lg rounded-2xl border overflow-hidden mb-8">
    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 h-36 relative">
      <Avatar
        size={100}
        src={child.profilePic}
        className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 border-4 border-white bg-gray-200"
      >
        {child.fullName?.charAt(0)}
      </Avatar>
    </div>
    <div className="pt-16 pb-6 px-6 text-center">
      <h2 className="text-2xl font-semibold text-gray-800">{child.fullName}</h2>
      <p className="text-sm text-gray-500">{child.email}</p>
    </div>
  </div>
);

const ParentDashboard = () => {
  const { user } = userStore();
  const [childrenList, setChildrenList] = useState([]);
  const [selectedChildEmail, setSelectedChildEmail] = useState(null);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [quizResults, setQuizResults] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 6, total: 0 });
  const hasFetchedChildren = useRef(false);
  const navigate = useNavigate();
  const selectedChild = childrenList.find(child => child.email === selectedChildEmail);

  useEffect(() => {
    const fetchChildren = async () => {
      if (hasFetchedChildren.current || !user?.email) return;
      hasFetchedChildren.current = true;
      setLoadingChildren(true);
      try {
        const res = await getChildsAPI(user.email);
        const childs = res?.data?.children || [];
        setChildrenList(childs);
        if (childs.length > 0) setSelectedChildEmail(childs[0].email);
      } catch (err) {
        antdMessage.error('Failed to fetch children');
      } finally {
        setLoadingChildren(false);
      }
    };
    fetchChildren();
  }, [user?.email]);

  useEffect(() => {
    if (selectedChildEmail) fetchResults(1, searchText);
  }, [selectedChildEmail]);

  const fetchResults = async (page = 1, search = '') => {
    setLoadingResults(true);
    try {
      const res = await getSingleStudentResultAPI({
        email: selectedChildEmail,
        search,
        page,
        limit: pagination.pageSize,
      });
      setQuizResults(res?.data || []);
      setPagination(prev => ({
        ...prev,
        current: res.currentPage || 1,
        total: res.totalResults || 0,
      }));
    } catch (err) {
      antdMessage.error('Failed to load results');
    } finally {
      setLoadingResults(false);
    }
  };

  const debouncedSearch = useCallback(debounce((value) => fetchResults(1, value), 1000), [pagination.pageSize, selectedChildEmail]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    if (!value.trim()) fetchResults(1, '');
    else debouncedSearch(value);
  };

  const handleChildChange = (value) => {
    setSelectedChildEmail(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
    fetchResults(page, searchText);
  };

  const handleQuizReport = (report) => {
    navigate('/view-chapter-report', { state: { report } });
  };

  return (
    <Content className="p-2">
      <AppHeader
        title={`Welcome, ${user?.fullName} 👋`}
        content="Monitor your children's academic performance in real-time with detailed insights."
      />

      <div className="relative bg-[#D55F5A] text-white p-4 rounded-xl flex justify-between mt-4">
        <div className="font-semibold flex items-center text-[18px] lg:text-[24px]">
          Stay informed, stay involved — <br /> because every quiz matters
        </div>
        <img src={studentbar} alt="studentbar" className="mr-4" />
      </div>

      <div className="bg-white/90 backdrop-blur rounded-3xl shadow-lg border p-6 sm:p-8 mt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <Title level={4} className="!mb-1 text-gray-800">Select Your Child</Title>
            <Text className="text-gray-500 text-sm">Explore detailed performance reports for each child.</Text>
          </div>
          <Select
            placeholder="Select Child"
            style={{ width: 260 }}
            onChange={handleChildChange}
            value={selectedChildEmail}
            loading={loadingChildren}
            size="large"
            className="w-full sm:w-auto"
          >
            {childrenList.map((child) => (
              <Option key={child.email} value={child.email}>
                <div className="flex items-center gap-2">
                  <Avatar src={child.profilePic}>{child.fullName?.charAt(0)}</Avatar>
                  {child.fullName}
                </div>
              </Option>
            ))}
          </Select>
        </div>

        <Divider />

        {loadingChildren ? (
          <Skeleton active avatar paragraph={{ rows: 4 }} />
        ) : !selectedChild ? (
          <div className="flex justify-center py-20">
            <Empty description="Please select a child to continue." />
          </div>
        ) : (
          <>
            <ChildProfileCard child={selectedChild} />

            <div className="flex justify-between items-center flex-col sm:flex-row mb-6 gap-3">
              <Title level={5} className="!mb-0 text-gray-700">Quiz Results</Title>
              <Input
                placeholder="🔍 Search subject or chapter..."
                allowClear
                onChange={handleSearchChange}
                value={searchText}
                size="large"
                className="w-full sm:w-80"
              />
            </div>

            <Divider />

            {loadingResults ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : quizResults.length === 0 ? (
              <Empty description="No quiz results found for this child." />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                  {quizResults.map((result, index) => (
                    <QuizCard key={index} result={result} selectedChild={selectedChild} onReport={handleQuizReport} />
                  ))}
                </div>
                {pagination.total > pagination.pageSize && (
                  <div className="flex justify-center mt-8">
                    <Pagination
                      current={pagination.current}
                      pageSize={pagination.pageSize}
                      total={pagination.total}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Content>
  );
};

export default ParentDashboard;
